// Data module for culinary experiences

// Définition des types pour les expériences culinaires
export interface CulinaryExperience {
    id: string;
    title: string;
    description: string;
    region?: string;
    mediaType: 'image' | 'video';
    mediaUrl: string;
    thumbnailUrl?: string;
    author?: string;
    email?: string;
    createdAt: Date;
    approved: boolean;
}

// Données statiques pour le développement/démo
// À remplacer par une vraie connexion à la base de données
const sampleCulinaryExperiences: CulinaryExperience[] = [
    {
        id: '1',
        title: 'Attiéké-Poisson à Abidjan',
        description: 'L\'attiéké-poisson est un plat emblématique de la Côte d\'Ivoire, particulièrement populaire à Abidjan. Ce plat combine la semoule de manioc fermentée (attiéké) avec du poisson grillé, des oignons, des tomates et du piment.',
        region: 'Abidjan',
        mediaType: 'image',
        mediaUrl: '/images/culinary/attieke-poisson.jpg',
        author: 'Office du Tourisme',
        createdAt: new Date('2023-05-15'),
        approved: true
    },
    {
        id: '2',
        title: 'Préparation du Foutou Banane',
        description: 'Le foutou banane est une pâte élastique obtenue en pilant des morceaux de banane plantain et d\'igname. Il est généralement servi avec une sauce claire ou graine.',
        region: 'Centre',
        mediaType: 'video',
        mediaUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        thumbnailUrl: '/images/culinary/foutou-banane-thumb.jpg',
        author: 'Chef Eba',
        createdAt: new Date('2023-06-22'),
        approved: true
    },
    {
        id: '3',
        title: 'Sauce Graine de Grand-Bassam',
        description: 'La sauce graine, préparée à partir de graines de palmier, est un délice culinaire ivoirien. À Grand-Bassam, elle est souvent préparée avec du poisson fumé et des épices locales.',
        region: 'Grand-Bassam',
        mediaType: 'image',
        mediaUrl: '/images/culinary/sauce-graine.jpg',
        author: 'Marie Konan',
        createdAt: new Date('2023-07-10'),
        approved: true
    },
    {
        id: '4',
        title: 'Le Garba de Treichville',
        description: 'Le garba est un plat de rue populaire composé d\'attiéké et de thon frit. À Treichville, ce plat est une institution et attire des visiteurs de tous horizons.',
        region: 'Treichville, Abidjan',
        mediaType: 'image',
        mediaUrl: '/images/culinary/garba.jpg',
        author: 'Office du Tourisme',
        createdAt: new Date('2023-08-05'),
        approved: true
    },
    {
        id: '5',
        title: 'Préparation du Kedjenou',
        description: 'Le kedjenou est un ragoût traditionnel ivoirien préparé avec du poulet et des légumes, cuits lentement dans une marmite en terre cuite.',
        region: 'Yamoussoukro',
        mediaType: 'video',
        mediaUrl: 'https://vimeo.com/76979871',
        thumbnailUrl: '/images/culinary/kedjenou-thumb.jpg',
        author: 'Chef Kouassi',
        createdAt: new Date('2023-09-18'),
        approved: true
    },
    {
        id: '6',
        title: 'Alloco de Bord de Mer',
        description: 'L\'alloco, ou banane plantain frite, est un en-cas populaire en Côte d\'Ivoire. Sur les plages, il est souvent servi avec du poisson grillé et une sauce pimentée.',
        region: 'Assinie',
        mediaType: 'image',
        mediaUrl: '/images/culinary/alloco.jpg',
        author: 'Office du Tourisme',
        createdAt: new Date('2023-10-30'),
        approved: true
    }
];

// Fonction pour récupérer toutes les expériences culinaires approuvées
// Utilisation de cache() pour optimiser les performances avec React Server Components
export const getCulinaryExperiences = async () => {
    // TODO: Remplacer par une vraie requête à la base de données
    // Exemple avec Prisma:
    // return prisma.culinaryExperience.findMany({
    //   where: { approved: true },
    //   orderBy: { createdAt: 'desc' }
    // });

    // Pour le moment, on retourne les données statiques
    return sampleCulinaryExperiences.filter(exp => exp.approved);
};

// Fonction pour récupérer une expérience culinaire par son ID
export async function getCulinaryExperienceById(id: string): Promise<CulinaryExperience | null> {
    // TODO: Remplacer par une vraie requête à la base de données
    // Exemple avec Prisma:
    // return prisma.culinaryExperience.findUnique({
    //   where: { id }
    // });

    const experience = sampleCulinaryExperiences.find(exp => exp.id === id);
    return experience || null;
}

// Fonction pour ajouter une nouvelle expérience culinaire
export async function addCulinaryExperience(data: Omit<CulinaryExperience, 'id' | 'createdAt' | 'approved'>) {
    // TODO: Remplacer par une vraie requête à la base de données
    // Exemple avec Prisma:
    // return prisma.culinaryExperience.create({
    //   data: {
    //     ...data,
    //     approved: false, // Les nouvelles soumissions doivent être approuvées
    //     createdAt: new Date()
    //   }
    // });

    // Pour le moment, on simule l'ajout
    const newExperience: CulinaryExperience = {
        id: `${sampleCulinaryExperiences.length + 1}`,
        ...data,
        createdAt: new Date(),
        approved: false // Les nouvelles soumissions doivent être approuvées
    };

    // Dans un environnement réel, vous ajouteriez à la base de données
    console.log('Nouvelle expérience culinaire soumise:', newExperience);

    return newExperience;
}

// Fonction pour approuver une expérience culinaire (pour les administrateurs)
export async function approveCulinaryExperience(id: string) {
    // TODO: Remplacer par une vraie requête à la base de données
    // Exemple avec Prisma:
    // return prisma.culinaryExperience.update({
    //   where: { id },
    //   data: { approved: true }
    // });

    // Pour le moment, on simule l'approbation
    const experienceIndex = sampleCulinaryExperiences.findIndex(exp => exp.id === id);
    if (experienceIndex !== -1) {
        sampleCulinaryExperiences[experienceIndex].approved = true;
        return sampleCulinaryExperiences[experienceIndex];
    }
    return null;
}

// Fonction pour supprimer une expérience culinaire
export async function deleteCulinaryExperience(id: string) {
    // TODO: Remplacer par une vraie requête à la base de données
    // Exemple avec Prisma:
    // return prisma.culinaryExperience.delete({
    //   where: { id }
    // });

    // Pour le moment, on simule la suppression
    const experienceIndex = sampleCulinaryExperiences.findIndex(exp => exp.id === id);
    if (experienceIndex !== -1) {
        const deleted = sampleCulinaryExperiences[experienceIndex];
        // Dans un environnement réel, vous supprimeriez de la base de données
        console.log('Expérience culinaire supprimée:', deleted);
        return deleted;
    }
    return null;
}

// Fonction pour rechercher des expériences culinaires
export async function searchCulinaryExperiences(query: string) {
    // TODO: Remplacer par une vraie requête à la base de données
    // Exemple avec Prisma:
    // return prisma.culinaryExperience.findMany({
    //   where: {
    //     AND: [
    //       { approved: true },
    //       {
    //         OR: [
    //           { title: { contains: query, mode: 'insensitive' } },
    //           { description: { contains: query, mode: 'insensitive' } },
    //           { region: { contains: query, mode: 'insensitive' } }
    //         ]
    //       }
    //     ]
    //   },
    //   orderBy: { createdAt: 'desc' }
    // });

    // Pour le moment, on simule la recherche
    const lowercaseQuery = query.toLowerCase();
    return sampleCulinaryExperiences.filter(
        exp => exp.approved && (
            exp.title.toLowerCase().includes(lowercaseQuery) ||
            exp.description.toLowerCase().includes(lowercaseQuery) ||
            (exp.region && exp.region.toLowerCase().includes(lowercaseQuery))
        )
    );
}
